import { AttentionSourceType, IAttentionRow } from "./types";

/**
 * The bands the full page splits the list into. The server already returns the
 * rows in the right order, so a band never reorders anything — it only draws a
 * line between "this was due already" and "this is not due for a while".
 */
export enum AttentionGroup {
    Overdue = "overdue",
    Soon = "soon",
    Later = "later",
}

export const attentionGroupOrder: AttentionGroup[] = [
    AttentionGroup.Overdue,
    AttentionGroup.Soon,
    AttentionGroup.Later,
];

export const attentionGroupHeadings: Record<AttentionGroup, string> = {
    [AttentionGroup.Overdue]: "ATTENTION_GROUP_OVERDUE",
    [AttentionGroup.Soon]: "ATTENTION_GROUP_SOON",
    [AttentionGroup.Later]: "ATTENTION_GROUP_LATER",
};

/**
 * The value of the <c>filter</c> query parameter. Short words rather than the
 * numeric source type, so a pasted link still means something a year from now
 * and keeps working if the numbers are ever renumbered.
 */
export const allFilter = "all";
export const overdueFilter = "overdue";

export const filterBySource: Record<AttentionSourceType, string> = {
    [AttentionSourceType.GroupInvite]: "invite",
    [AttentionSourceType.NewsAcknowledgement]: "acknowledge",
    [AttentionSourceType.LearningCourse]: "course",
    [AttentionSourceType.FormResponse]: "form",
};

/**
 * The order the filter chips appear in, most obliging first. Exhaustive, so adding
 * a source without deciding where its chip goes does not compile.
 */
const filterOrder: Record<AttentionSourceType, number> = {
    [AttentionSourceType.LearningCourse]: 1,
    [AttentionSourceType.NewsAcknowledgement]: 2,
    [AttentionSourceType.FormResponse]: 3,
    [AttentionSourceType.GroupInvite]: 4,
};

export const filterLabelBySource: Record<AttentionSourceType, string> = {
    [AttentionSourceType.GroupInvite]: "ATTENTION_FILTER_INVITES",
    [AttentionSourceType.NewsAcknowledgement]: "ATTENTION_FILTER_ACKNOWLEDGEMENTS",
    [AttentionSourceType.LearningCourse]: "ATTENTION_FILTER_COURSES",
    [AttentionSourceType.FormResponse]: "ATTENTION_FILTER_FORMS",
};

export interface IAttentionFilter {
    key: string;
    label: string;
    count: number;
}

export interface IAttentionGroup {
    key: AttentionGroup;
    heading: string;
    rows: IAttentionRow[];
}

/**
 * Which band a row belongs in.
 *
 * "Soon" is today and tomorrow by the reader's own calendar, not the next 48
 * hours: something due at 07:00 tomorrow should not drop out of the band just
 * because it is now past 07:00 today. A row with no deadline can never be late
 * and is never urgent, so it sits at the back.
 */
export function groupOf(row: IAttentionRow, now: Date = new Date()): AttentionGroup {
    if (row.isOverdue) {
        return AttentionGroup.Overdue;
    }

    if (!row.dueAt) {
        return AttentionGroup.Later;
    }

    const dayAfterTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);

    return new Date(row.dueAt) < dayAfterTomorrow
        ? AttentionGroup.Soon
        : AttentionGroup.Later;
}

export function matchesFilter(row: IAttentionRow, filter: string): boolean {
    if (filter === allFilter) {
        return true;
    }

    if (filter === overdueFilter) {
        return row.isOverdue;
    }

    return filterBySource[row.sourceType] === filter;
}

/**
 * The chips, in the order the design puts them: everything, then what is late,
 * then one per kind that is actually present. A kind with nothing in it gets no
 * chip rather than a chip reading zero.
 */
export function buildFilters(rows: IAttentionRow[]): IAttentionFilter[] {
    const filters: IAttentionFilter[] = [
        { key: allFilter, label: "ATTENTION_FILTER_ALL", count: rows.length },
    ];

    const overdueCount = rows.filter((row) => row.isOverdue).length;

    if (overdueCount > 0) {
        filters.push({ key: overdueFilter, label: "ATTENTION_FILTER_OVERDUE", count: overdueCount });
    }

    // A Record rather than an array, so a new source that is given no place here
    // fails the build. As a list it silently lost its chip, which is how the form
    // answers shipped without one.
    const sourceTypes = (Object.keys(filterOrder) as unknown as AttentionSourceType[])
        .map((key) => Number(key) as AttentionSourceType)
        .sort((a, b) => filterOrder[a] - filterOrder[b]);

    for (const sourceType of sourceTypes) {
        const count = rows.filter((row) => row.sourceType === sourceType).length;

        if (count > 0) {
            filters.push({
                key: filterBySource[sourceType],
                label: filterLabelBySource[sourceType],
                count,
            });
        }
    }

    return filters;
}

/**
 * The rows that survive the filter, split into bands. Empty bands are dropped so
 * a filter never leaves a heading standing over nothing.
 */
export function buildGroups(
    rows: IAttentionRow[],
    filter: string,
    now: Date = new Date(),
): IAttentionGroup[] {
    const groups: IAttentionGroup[] = [];

    for (const key of attentionGroupOrder) {
        const groupRows = rows.filter(
            (row) => matchesFilter(row, filter) && groupOf(row, now) === key);

        if (groupRows.length > 0) {
            groups.push({ key, heading: attentionGroupHeadings[key], rows: groupRows });
        }
    }

    return groups;
}

/**
 * A filter read off the query string. Anything we do not recognise — an old
 * link, a typo — falls back to showing everything rather than an empty page.
 */
export function readFilter(search: string, filters: IAttentionFilter[]): string {
    const value = new URLSearchParams(search).get("filter");

    return value && filters.some((filter) => filter.key === value) ? value : allFilter;
}
